import React from "react";
import { Line } from "react-chartjs-2";
import { ChevronDown, Download, FileSpreadsheet, FileText, LineChart } from "lucide-react";
import tinycolor from "tinycolor2";
import { useTheme } from "../../CommonComponents/ThemeProvider";

const SalesChart = ({
    salesByDayLabels = [],
    salesByDayData = [],
    analytics,
    selectedMonth,
    selectedMonthLabel,
}) => {
    const { currentTheme } = useTheme();
    const exportRef = React.useRef(null);
    const [exportMenuOpen, setExportMenuOpen] = React.useState(false);
    const accentColor = currentTheme?.color || "var(--color-primary)";
    const accentTint = tinycolor(currentTheme?.color || "#0f172a").setAlpha(0.1).toRgbString();

    const getSalesRows = () => {
        const ordersByDate = (analytics?.salesByDay || []).reduce((acc, item) => {
            const key = String(item.date || "").slice(0, 10);
            if (key) acc[key] = item.ordersCount ?? "";
            return acc;
        }, {});

        return salesByDayLabels.map((label, index) => {
            const date = String(label || "").slice(0, 10);
            return {
                date,
                sales: Number(salesByDayData[index] ?? 0),
                orders: Number(ordersByDate[date] ?? 0),
            };
        });
    };

    const formatCurrency = (value) => `PHP ${Number(value || 0).toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

    const downloadFile = (content, type, filename) => {
        const blob = new Blob([content], { type });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const exportSalesData = () => {
        const rows = getSalesRows();
        const csvRows = ["Date,Sales,Orders"];
        rows.forEach((row) => {
            csvRows.push(`${row.date},${row.sales},${row.orders}`);
        });

        downloadFile(csvRows.join("\n"), "text/csv;charset=utf-8", `sales-${selectedMonth || "month"}.csv`);
        setExportMenuOpen(false);
    };

    const exportSalesPdf = async () => {
        const { default: jsPDF } = await import("jspdf");
        const rows = getSalesRows();
        const rawThemeColor = currentTheme?.color || "#0f172a";
        const themeColor = tinycolor(rawThemeColor).isValid()
            ? tinycolor(rawThemeColor).toHexString()
            : "#0f172a";
        const softTheme = tinycolor(themeColor).lighten(43).toHexString();
        const totalSales = rows.reduce((sum, row) => sum + row.sales, 0);
        const totalOrders = rows.reduce((sum, row) => sum + row.orders, 0);
        const bestDay = rows.reduce((best, row) => (row.sales > (best?.sales || 0) ? row : best), null);
        const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 42;
        const summaryTop = 140;
        const summaryHeight = 54;
        const summaryGap = 12;
        const summaryWidth = (pageWidth - margin * 2 - summaryGap * 2) / 3;
        const tableTop = 216;
        const rowHeight = 24;
        let y = tableTop;

        const drawHeader = () => {
            doc.setFillColor(themeColor);
            doc.roundedRect(margin, 32, pageWidth - margin * 2, 92, 18, 18, "F");
            doc.setTextColor("#ffffff");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(20);
            doc.text("Sales Analytics Report", margin + 24, 70);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.text(`Daily gross sales for ${selectedMonthLabel || selectedMonth || "selected month"}`, margin + 24, 92);
            doc.setFont("helvetica", "bold");
            doc.text("ZEROONE POS", pageWidth - margin - 104, 70);
        };

        const drawSummaryCard = (x, title, value, note) => {
            doc.setDrawColor("#e2e8f0");
            doc.setFillColor(softTheme);
            doc.roundedRect(x, summaryTop, summaryWidth, summaryHeight, 12, 12, "FD");
            doc.setTextColor("#64748b");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(8);
            doc.text(title.toUpperCase(), x + 14, summaryTop + 18);
            doc.setTextColor("#0f172a");
            doc.setFontSize(13);
            doc.text(value, x + 14, summaryTop + 34);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor("#64748b");
            doc.text(note, x + 14, summaryTop + 47);
        };

        const drawTableHeader = () => {
            doc.setFillColor(themeColor);
            doc.roundedRect(margin, y, pageWidth - margin * 2, 28, 10, 10, "F");
            doc.setTextColor("#ffffff");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(9);
            doc.text("DATE", margin + 16, y + 18);
            doc.text("SALES", margin + 220, y + 18);
            doc.text("ORDERS", margin + 390, y + 18);
            y += 34;
        };

        const drawFooter = () => {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor("#94a3b8");
            doc.text(`Generated ${new Date().toLocaleString("en-PH")}`, margin, pageHeight - 26);
        };

        drawHeader();
        drawSummaryCard(margin, "Total sales", formatCurrency(totalSales), selectedMonthLabel || "Selected month");
        drawSummaryCard(margin + summaryWidth + summaryGap, "Orders", String(totalOrders), "Paid transactions");
        drawSummaryCard(margin + (summaryWidth + summaryGap) * 2, "Best day", bestDay?.date || "No sales", bestDay ? formatCurrency(bestDay.sales) : "PHP 0.00");
        drawTableHeader();

        rows.forEach((row, index) => {
            if (y + rowHeight > pageHeight - 52) {
                drawFooter();
                doc.addPage();
                y = 42;
                drawTableHeader();
            }

            doc.setFillColor(index % 2 === 0 ? "#ffffff" : "#f8fafc");
            doc.roundedRect(margin, y - 6, pageWidth - margin * 2, rowHeight, 8, 8, "F");
            doc.setTextColor("#0f172a");
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.text(row.date || "-", margin + 16, y + 10);
            doc.text(formatCurrency(row.sales), margin + 220, y + 10);
            doc.text(String(row.orders), margin + 390, y + 10);
            y += rowHeight;
        });

        if (!rows.length) {
            doc.setTextColor("#64748b");
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.text("No sales data available for this month.", margin + 16, y + 12);
        }

        drawFooter();
        doc.save(`sales-${selectedMonth || "month"}.pdf`);
        setExportMenuOpen(false);
    };

    React.useEffect(() => {
        const handlePointerDown = (event) => {
            if (!exportRef.current?.contains(event.target)) setExportMenuOpen(false);
        };

        const handleKeyDown = (event) => {
            if (event.key === "Escape") setExportMenuOpen(false);
        };

        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    return (
        <section className="flex w-full flex-col rounded-[30px] border border-slate-200 bg-white p-5 shadow-[0_24px_70px_rgba(15,23,42,0.08)]">
            <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                    <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                        <LineChart className="h-4 w-4" />
                        Monthly Sales
                    </div>
                    <h2 className="mt-3 text-xl font-semibold tracking-tight text-slate-950">Sales Trend</h2>
                    <p className="mt-1 text-sm text-slate-500">Daily gross sales for {selectedMonthLabel}.</p>
                </div>
                <div ref={exportRef} className="relative">
                    <button
                        type="button"
                        className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none"
                        aria-label="Download sales data"
                        aria-haspopup="menu"
                        aria-expanded={exportMenuOpen}
                        onClick={() => setExportMenuOpen((prev) => !prev)}
                    >
                        <Download className="h-4 w-4" style={{ color: accentColor }} />
                        <ChevronDown className={`h-3.5 w-3.5 text-slate-500 transition ${exportMenuOpen ? "rotate-180" : ""}`} />
                    </button>

                    {exportMenuOpen && (
                        <div
                            className="absolute right-0 top-full z-30 mt-2 w-48 overflow-hidden rounded-2xl border border-slate-200 bg-white p-1.5 shadow-[0_18px_45px_rgba(15,23,42,0.16)]"
                            role="menu"
                        >
                            <button
                                type="button"
                                onClick={exportSalesData}
                                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                                role="menuitem"
                            >
                                <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl" style={{ background: accentTint }}>
                                    <FileSpreadsheet className="h-4 w-4" style={{ color: accentColor }} />
                                </span>
                                Excel CSV
                            </button>
                            <button
                                type="button"
                                onClick={exportSalesPdf}
                                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                                role="menuitem"
                            >
                                <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl" style={{ background: accentTint }}>
                                    <FileText className="h-4 w-4" style={{ color: accentColor }} />
                                </span>
                                PDF report
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="h-80 w-full rounded-[24px] border border-slate-100 bg-slate-50/50 p-3">
                <Line
                    data={{
                        labels: salesByDayLabels,
                        datasets: [
                            {
                                label: selectedMonthLabel,
                                data: salesByDayData,
                                borderColor: accentColor,
                                backgroundColor: tinycolor(currentTheme?.color || "#0f172a").setAlpha(0.08).toRgbString(),
                                pointBackgroundColor: accentColor,
                                pointBorderColor: accentColor,
                                pointRadius: 3,
                                pointHoverRadius: 5,
                                fill: true,
                                tension: 0.45,
                            },
                        ],
                    }}
                    options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: { display: false },
                            title: { display: false },
                            tooltip: {
                                backgroundColor: "#fff",
                                titleColor: "#222",
                                bodyColor: "#222",
                                borderColor: "#e5e7eb",
                                borderWidth: 1,
                                titleFont: { size: 13, weight: "bold" },
                                bodyFont: { size: 13 },
                                padding: 10,
                            },
                        },
                        scales: {
                            x: {
                                title: { display: true, text: "Day", font: { size: 13 }, color: "#6B7280" },
                                grid: {
                                    display: true,
                                    color: "#F3F4F6",
                                    borderDash: [4, 4],
                                    lineWidth: 1,
                                },
                                ticks: { font: { size: 13 }, color: "#6B7280" },
                            },
                            y: {
                                title: { display: true, text: "Sales (PHP)", font: { size: 13 }, color: "#6B7280" },
                                grid: { display: false },
                                ticks: { font: { size: 13 }, color: "#6B7280", callback: value => `PHP ${value}` },
                            },
                        },
                    }}
                />
            </div>
        </section>
    );
};

export default SalesChart;
